import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Edit, Save, FileText, Image } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_OPTIONS = [
  { value: 'home', label: 'Startseite' },
  { value: 'about', label: 'Über mich' },
  { value: 'services', label: 'Therapieangebote' },
  { value: 'practice', label: 'Praxiseinblick' },
  { value: 'courses', label: 'Kursangebote' },
];

export default function AdminContent() {
  const [content, setContent] = useState([]);
  const [selectedPage, setSelectedPage] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Inhalte');
    } finally {
      setLoading(false);
    }
  };

  const filteredContent = selectedPage === 'all' 
    ? content 
    : content.filter(item => item.page === selectedPage);

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditForm({
      title: item.title || '',
      content: item.content || '',
      image_url: item.image_url || ''
    });
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditForm({ title: '', content: '', image_url: '' });
  };

  const saveEdit = async () => {
    if (!editingItem) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/content/${editingItem.id}`, editForm, getAuthHeader());
      toast.success('Inhalt erfolgreich gespeichert!');
      fetchContent();
      closeEditDialog();
    } catch (error) {
      toast.error('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
    } finally {
      setSaving(false);
    }
  };

  const getPageLabel = (page) => {
    return PAGE_OPTIONS.find(p => p.value === page)?.label || page;
  };

  const getSectionLabel = (section) => {
    const labels = {
      'hero': 'Kopfbereich',
      'quote': 'Zitat',
      'quote_author': 'Zitat-Autor',
      'intro': 'Einleitung',
      'card_about': 'Karte: Über mich',
      'card_therapy': 'Karte: Therapie',
      'card_practice': 'Karte: Praxis',
      'title': 'Titel',
      'subtitle': 'Untertitel',
      'werdegang': 'Werdegang',
      'qualifikationen': 'Qualifikationen',
      'conditions': 'Erkrankungen',
      'private_services': 'Privatleistungen',
      'methods': 'Methoden',
      'ablauf': 'Ablauf',
      'therapy_types': 'Therapieformen',
      'costs_gkv': 'Kosten GKV',
      'costs_pkv': 'Kosten PKV',
      'costs_self': 'Selbstzahler',
      'accessibility': 'Barrierefreiheit'
    };
    return labels[section] || section;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-content-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            Seiteninhalte
          </h1>
          <p className="text-gray-500 mt-2">
            Bearbeiten Sie die Texte und Bilder auf Ihrer Website
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedPage} onValueChange={setSelectedPage}>
            <SelectTrigger className="w-48 bg-white text-gray-800 border-gray-300" data-testid="page-filter">
              <SelectValue placeholder="Seite filtern" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Alle Seiten</SelectItem>
              {PAGE_OPTIONS.map(page => (
                <SelectItem key={page.value} value={page.value}>
                  {page.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="bg-white">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px] text-gray-700">Seite</TableHead>
                <TableHead className="w-[160px] text-gray-700">Bereich</TableHead>
                <TableHead className="text-gray-700">Titel</TableHead>
                <TableHead className="max-w-[300px] text-gray-700">Inhalt (Vorschau)</TableHead>
                <TableHead className="w-[100px] text-center text-gray-700">Bearbeiten</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContent.map((item) => (
                <TableRow
                  key={item.id}
                  data-testid={`content-row-${item.id}`}
                  className="cursor-pointer hover:bg-gray-50 bg-white"
                  onClick={() => openEditDialog(item)}
                >
                  <TableCell className="font-medium text-gray-800">
                    {getPageLabel(item.page)}
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {getSectionLabel(item.section)}
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-gray-800">
                      {item.title || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[300px]">
                    <p className="text-sm text-gray-600 truncate">
                      {item.content?.substring(0, 80)}...
                    </p>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(item);
                      }}
                      className="text-teal-600 border-teal-600 hover:bg-teal-600 hover:text-white"
                      data-testid={`edit-btn-${item.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Ändern
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog - User-friendly modal */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-800">
              Inhalt bearbeiten
            </DialogTitle>
            {editingItem && (
              <p className="text-sm text-gray-500">
                {getPageLabel(editingItem.page)} → {getSectionLabel(editingItem.section)}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Title Field */}
            <div className="space-y-2">
              <Label htmlFor="edit-title" className="text-gray-700 font-medium">
                Titel
              </Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Titel eingeben..."
                className="text-lg bg-white text-gray-800 border-gray-300"
                data-testid="edit-title-input"
              />
              <p className="text-xs text-gray-500">
                Der Titel wird als Überschrift angezeigt (kann leer bleiben)
              </p>
            </div>

            {/* Content Field */}
            <div className="space-y-2">
              <Label htmlFor="edit-content" className="text-gray-700 font-medium">
                Inhalt / Text
              </Label>
              <Textarea
                id="edit-content"
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                placeholder="Text eingeben..."
                rows={8}
                className="text-base leading-relaxed bg-white text-gray-800 border-gray-300"
                data-testid="edit-content-input"
              />
              <p className="text-xs text-gray-500">
                Tipp: Bei Listen (z.B. Qualifikationen) trennen Sie die Punkte mit dem <code className="bg-gray-100 px-1 rounded text-gray-700">|</code> Zeichen
              </p>
            </div>

            {/* Image URL Field */}
            <div className="space-y-2">
              <Label htmlFor="edit-image" className="text-gray-700 font-medium flex items-center gap-2">
                <Image className="h-4 w-4" />
                Bild-URL (optional)
              </Label>
              <Input
                id="edit-image"
                value={editForm.image_url}
                onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                placeholder="https://beispiel.de/bild.jpg"
                className="bg-white text-gray-800 border-gray-300"
                data-testid="edit-image-input"
              />
              {editForm.image_url && (
                <div className="mt-2 p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500 mb-2">Vorschau:</p>
                  <img
                    src={editForm.image_url}
                    alt="Vorschau"
                    className="max-h-32 rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 border-t pt-4">
            <Button
              variant="outline"
              onClick={closeEditDialog}
              disabled={saving}
              className="text-gray-700 border-gray-300"
            >
              Abbrechen
            </Button>
            <Button
              onClick={saveEdit}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="save-edit-btn"
            >
              {saving ? (
                <>Speichern...</>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Section */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-600" />
          So bearbeiten Sie Inhalte
        </h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Klicken Sie auf eine Zeile oder den "Ändern"-Button</li>
          <li>• Bearbeiten Sie Titel und Text im Dialog</li>
          <li>• Fügen Sie optional eine Bild-URL hinzu</li>
          <li>• Klicken Sie auf "Speichern" um die Änderungen zu übernehmen</li>
        </ul>
      </div>
    </div>
  );
}
